import clsx from "clsx"

import { Text, Image } from "@/elements"
import pluralize from "pluralize"
import { FragmentType, graphql, useFragment } from "@/gql"
import {
  AdditionalProjectsForApplication_V2FragmentDoc,
  AdditionalProjectsForApplicationFragmentDoc,
} from "@/gql/graphql"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"

graphql(`
  fragment AdditionalProjectsForApplication_V2 on IndividualCreative {
    allWorks: creativeWorkPortfolioProjects(
      first: 9
      filters: { status: { exact: PUBLISHED } }
      order: { profileOrder: ASC }
    ) {
      totalCount
      edges {
        node {
          id
          thumbnail645w: thumbnailUrl(width: 645)
          thumbnailFocalPointX
          thumbnailFocalPointY
        }
      }
    }
  }
  fragment AdditionalProjectsForApplication on IndividualCreative {
    allWorks: creativeWorkPortfolioProjects(first: 9, status: PUBLISHED, orderBy: "profileOrder") {
      totalCount
      edges {
        node {
          id
          thumbnail645w: thumbnailUrl(width: 645)
          thumbnailFocalPointX
          thumbnailFocalPointY
        }
      }
    }
  }
`)

interface Props {
  creative:
    | FragmentType<typeof AdditionalProjectsForApplicationFragmentDoc>
    | FragmentType<typeof AdditionalProjectsForApplication_V2FragmentDoc>
    | null
    | undefined
  selectedProjectsIds: string[]
  minHeight?: string
  width?: string
  textClassName?: string
  gqlVersion?: 1 | 2
}

export const AdditionalProjectsCard: React.FC<Props> = ({
  selectedProjectsIds,
  minHeight = "286px",
  width = "auto",
  textClassName,
  gqlVersion = 1,
  ...props
}) => {
  const creativeV1 =
    gqlVersion === 1
      ? getFragmentData(
          AdditionalProjectsForApplicationFragmentDoc,
          props.creative as FragmentType<typeof AdditionalProjectsForApplicationFragmentDoc>
        )
      : null

  const creativeV2 =
    gqlVersion === 2
      ? getFragmentData(
          AdditionalProjectsForApplication_V2FragmentDoc,
          props.creative as FragmentType<typeof AdditionalProjectsForApplication_V2FragmentDoc>
        )
      : null

  const creative = creativeV1 || creativeV2

  const plusMoreSection = creative?.allWorks.edges
    .filter(x => !selectedProjectsIds.includes(x?.node.id || ""))
    .slice(0, 3)
  if (plusMoreSection?.length === 0) {
    return null
  }

  return (
    <div
      style={{
        minHeight: minHeight,
        width: width,
      }}
      className="group/card relative h-auto min-w-[200px] shrink-0 rounded-md bg-bg-2">
      <div className="relative isolate flex flex-col items-center justify-end">
        <div
          className={clsx(
            "absolute top-9 z-20 size-[137px] overflow-hidden rounded-sm shadow-md transition-all group-hover/card:-translate-y-4 group-hover/card:translate-x-3 group-hover/card:rotate-12"
          )}>
          <Image
            srcSetUrls={{
              sm: plusMoreSection?.[0]?.node.thumbnail645w || "",
            }}
            alt="Project thumbnail"
            className="h-full w-full object-cover"
          />
        </div>
        {plusMoreSection?.[1]?.node.thumbnail645w ? (
          <div
            className={clsx(
              "absolute top-[62px] z-10 size-[117px] overflow-hidden rounded-sm shadow-sm transition-all group-hover/card:-translate-x-6 group-hover/card:-rotate-12"
            )}>
            <Image
              srcSetUrls={{
                sm: plusMoreSection?.[1]?.node.thumbnail645w || "",
              }}
              alt="Project thumbnail"
              className="h-full w-full object-cover"
            />
          </div>
        ) : null}
        {plusMoreSection?.[2]?.node.thumbnail645w ? (
          <div
            className={clsx(
              "absolute top-[91px] size-[94px] overflow-hidden rounded-sm shadow-sm transition-all group-hover/card:translate-y-4"
            )}>
            <Image
              srcSetUrls={{
                sm: plusMoreSection?.[2]?.node.thumbnail645w || "",
              }}
              alt="Project thumbnail"
              className="h-full w-full object-cover"
            />
          </div>
        ) : null}
      </div>
      <Text variant="body2" className={clsx("absolute bottom-10 w-full text-center", textClassName)}>
        +{creative?.allWorks.totalCount ? creative?.allWorks.totalCount - selectedProjectsIds.length : 0} other{" "}
        {pluralize(
          "project",
          creative?.allWorks.totalCount ? creative?.allWorks.totalCount - selectedProjectsIds.length : 0
        )}
      </Text>
    </div>
  )
}
